import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { ADMIN_NAV_SECTIONS, filterNavSections } from '../../../config/adminNavigation';
import { getUserRole } from '../../../utils/adminRBAC';

export function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const userRole = getUserRole('superadmin');
  const filteredSections = filterNavSections(ADMIN_NAV_SECTIONS, userRole);

  const isActive = (path: string) => {
    if (path === '/admin' && location.pathname === '/admin') return true;
    if (path !== '/admin' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      <aside
        className={`
          fixed top-0 left-0 h-screen bg-white border-r border-gray-200
          transition-all duration-300 z-30 flex flex-col
          ${collapsed ? 'w-20' : 'w-64'}
        `}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-white" size={20} />
              </div>
              <div>
                <span className="text-lg font-bold text-gray-900">TrezoOne</span>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center mx-auto">
              <TrendingUp className="text-white" size={20} />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-3">
          <nav className="space-y-6">
            {filteredSections.map((section) => (
              <div key={section.id}>
                {!collapsed && (
                  <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {section.label}
                  </p>
                )}
                {collapsed && (
                  <div className="h-px bg-gray-200 mb-2"></div>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <button
                        key={item.id}
                        onClick={() => navigate(item.path)}
                        disabled={item.comingSoon}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                          transition-all duration-150 group relative
                          ${active
                            ? 'bg-blue-50 text-blue-700'
                            : item.comingSoon
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-50'
                          }
                        `}
                        title={collapsed ? item.label : undefined}
                      >
                        <Icon size={20} className="flex-shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left">{item.label}</span>
                            {item.comingSoon && (
                              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                Bientôt
                              </span>
                            )}
                          </>
                        )}
                        {collapsed && item.comingSoon && (
                          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                            Bientôt disponible
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </div>

        <div className="border-t border-gray-200 p-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            title={collapsed ? 'Agrandir' : 'Réduire'}
          >
            {collapsed ? (
              <ChevronRight size={20} />
            ) : (
              <>
                <ChevronLeft size={20} />
                <span>Réduire</span>
              </>
            )}
          </button>
        </div>
      </aside>

      <div className={`transition-all duration-300 ${collapsed ? 'ml-20' : 'ml-64'}`}></div>
    </>
  );
}
